import { CREATE_OR_UPDATE_SOLICITUDES_AUTOMATICAS } from "@/apollo/mutations/epp";
import {
  GET_LIST_TIPO_PERIODOS_EPP,
  GET_LIST_TIPO_VALIDACIONES_EPP,
  FIND_ALL_CONDICIONES_SOLICITUDES_AUTOMATICAS,
} from "@/apollo/queries/epp";
import {
  GET_CARGOS,
  GET_SUCURSALES,
  GET_WORK_SHIFTS,
  GET_SINDICATO,
} from "@/apollo/queries";
import { exMut, exQue } from "@/utils/gql";

export const AutomatizacionState = {
  autoTipoPeriodos: [],
  autoTipoValidaciones: [],
  autoCondiciones: [],
  autoLoading: false,
  autoCargos: [],
  autoSucursales: [],
  autoTurnos: [],
  autoSindicatos: [],
};

export const AutomatizacionMutations = {
  setAutoTipoPeriodos(state, data) {
    state.autoTipoPeriodos = data;
  },
  setAutoTipoValidaciones(state, data) {
    state.autoTipoValidaciones = data;
  },
  setAutoCondiciones(state, data) {
    state.autoCondiciones = data;
  },
  setAutoLoading(state, isLoading) {
    state.autoLoading = isLoading;
  },
  setAutoCargos(state, data) {
    state.autoCargos = data;
  },
  setAutoSucursales(state, data) {
    state.autoSucursales = data;
  },
  setAutoTurnos(state, data) {
    state.autoTurnos = data;
  },
  setAutoSindicatos(state, data) {
    state.autoSindicatos = data;
  },
};

export const AutomatizacionActions = {
  /**
   * Obtiene la lista de tipos de periodos EPP.
   *
   * @param {Object} context - Contexto de Vuex.
   * @returns {Promise<Array<{ id_tipo: number, nombre: string, cantidad_dias: number }>|null>}
   */
  async getAutoTipoPeriodos({ commit }) {
    commit("setAutoLoading", true);
    try {
      const response = await exQue(GET_LIST_TIPO_PERIODOS_EPP, {}, true);
      commit("setAutoTipoPeriodos", Array.isArray(response) ? response : []);
      return response;
    } finally {
      commit("setAutoLoading", false);
    }
  },

  /**
   * Obtiene la lista de tipos de validaciones EPP.
   *
   * @param {Object} context - Contexto de Vuex.
   * @returns {Promise<Array<{ id_tipo: number, nombre: string, parametro: string }>|null>}
   */
  async getAutoTipoValidaciones({ commit }) {
    commit("setAutoLoading", true);
    try {
      const response = await exQue(GET_LIST_TIPO_VALIDACIONES_EPP, {}, true);
      commit("setAutoTipoValidaciones", Array.isArray(response) ? response : []);
      return response;
    } finally {
      commit("setAutoLoading", false);
    }
  },

  /**
   * Obtiene todas las condiciones de solicitudes automáticas de un formulario EPP.
   *
   * @param {Object} context - Contexto de Vuex.
   * @param {number|{ idFormulario?: number }} idFormulario - ID del formulario EPP.
   * @returns {Promise<Array<Object>|null>}
   */
  async getAutoCondiciones({ commit, state, dispatch }, idFormulario) {
    let resolvedIdFormulario = Number(
      typeof idFormulario === "object" && idFormulario !== null
        ? idFormulario.idFormulario
        : idFormulario,
    );

    commit("setAutoLoading", true);
    try {
      if (
        !Number.isInteger(resolvedIdFormulario) ||
        resolvedIdFormulario <= 0
      ) {
        await dispatch("ensureFormEpp");

        resolvedIdFormulario = Number(state.formEpp?.id_formulario);
      }

      if (!Number.isInteger(resolvedIdFormulario) || resolvedIdFormulario <= 0) {
        commit("setAutoCondiciones", []);
        return [];
      }

      const response = await exQue(
        FIND_ALL_CONDICIONES_SOLICITUDES_AUTOMATICAS,
        { idFormulario: resolvedIdFormulario },
        true,
      );
      commit("setAutoCondiciones", Array.isArray(response) ? response : []);
      return response;
    } finally {
      commit("setAutoLoading", false);
    }
  },


  /**
   * Crea o actualiza las solicitudes automáticas de un formulario EPP.
   *
   * @param {Object} context - Contexto de Vuex.
   * @param {Object} payload - Parámetros de la mutación.
   * @param {number} payload.idFormulario - ID del formulario EPP.
   * @param {Array<{
   *   id_periodo?: number,
   *   id_periodo_empresa?: number,
   *   productos?: number[],
   *   ejecuciones?: Array<{
   *     dias_ejecuciones?: number[], // 1 - 28
   *     fechas_ejecuciones?: string[] // DD-MM
   *   }>,
   *   validaciones?: Array<{
   *     id_validacion?: number,
   *     c_cargo?: number[],
   *     c_area?: number[],
   *     c_sucursal?: number[],
   *     c_turno?: number[],
   *     c_sindicatos?: number[],
   *     c_nacimiento?: { desde: number, hasta: number }[],
   *     c_ingreso?: { desde: number, hasta: number }[]
   *   }>
   * }>} payload.periodos - Lista de periodos con sus validaciones, productos y ejecuciones.
   * @returns {Promise<Object|null>} Resultado de la mutación o null si falla.
   */
  /**
   * Carga los selectores auxiliares (cargos, sucursales, turnos, sindicatos).
   *
   * @param {Object} context - Contexto de Vuex.
   * @param {string|number} id_empresa - ID de la empresa.
   */
  async loadAutoSelectores({ commit, state }, id_empresa) {
    const promises = [];
    
    if (!state.autoCargos.length) {
      promises.push(
        exQue(GET_CARGOS, { id_empresa }).then((res) => commit("setAutoCargos", res || []))
      );
    }
    if (!state.autoSucursales.length) {
      promises.push(
        exQue(GET_SUCURSALES, { id_empresa }).then((res) => commit("setAutoSucursales", res || []))
      );
    }
    if (!state.autoTurnos.length) {
      promises.push(
        exQue(GET_WORK_SHIFTS, { id_empresa }).then((res) => commit("setAutoTurnos", res || []))
      );
    }
    if (!state.autoSindicatos.length) {
      promises.push(
        exQue(GET_SINDICATO, { id_empresa }).then((res) => commit("setAutoSindicatos", res || []))
      );
    }
    await Promise.all(promises);
  },

  async createOrUpdateSolicitudesAutomaticas(
    { state, dispatch },
    { idFormulario, periodos },
  ) {
    let resolvedIdFormulario = Number(idFormulario);
    if (!Number.isInteger(resolvedIdFormulario) || resolvedIdFormulario <= 0) {
      await dispatch("ensureFormEpp");
      resolvedIdFormulario = Number(state.formEpp?.id_formulario);
    }

    return await exMut(
      CREATE_OR_UPDATE_SOLICITUDES_AUTOMATICAS,
      { idFormulario: resolvedIdFormulario, periodos },
      "Automatización guardada correctamente",
      "No se pudo guardar la automatización",
    );
  },
};
